function solveLinearSystem(A, B) {
  const n = 8;
  for (let i = 0; i < n; i++) {
    let maxEl = Math.abs(A[i][i]);
    let maxRow = i;
    for (let k = i + 1; k < n; k++) {
      if (Math.abs(A[k][i]) > maxEl) {
        maxEl = Math.abs(A[k][i]);
        maxRow = k;
      }
    }
    const tempA = A[maxRow]; A[maxRow] = A[i]; A[i] = tempA;
    const tempB = B[maxRow]; B[maxRow] = B[i]; B[i] = tempB;
    for (let k = i + 1; k < n; k++) {
      const c = -A[k][i] / A[i][i];
      for (let j = i; j < n; j++) {
        if (i === j) A[k][j] = 0;
        else A[k][j] += c * A[i][j];
      }
      B[k] += c * B[i];
    }
  }
  const x = new Array(n).fill(0);
  for (let i = n - 1; i >= 0; i--) {
    let sum = 0;
    for (let j = i + 1; j < n; j++) sum += A[i][j] * x[j];
    x[i] = (B[i] - sum) / A[i][i];
  }
  return x;
}

function getInverseHomography(srcCorners, srcW, srcH, dstW, dstH) {
  const s = srcCorners.map(p => ({ x: p.x * srcW, y: p.y * srcH }));
  const d = [{ x: 0, y: 0 }, { x: dstW, y: 0 }, { x: dstW, y: dstH }, { x: 0, y: dstH }];
  const A = []; const B = [];
  for (let i = 0; i < 4; i++) {
    const dx = d[i].x, dy = d[i].y, sx = s[i].x, sy = s[i].y;
    A.push([dx, dy, 1, 0, 0, 0, -dx * sx, -dy * sx]); B.push(sx);
    A.push([0, 0, 0, dx, dy, 1, -dx * sy, -dy * sy]); B.push(sy);
  }
  const h = solveLinearSystem(A, B);
  h.push(1);
  return h;
}

export function warpPerspective(srcCtx, srcW, srcH, dstCanvas, srcCorners) {
  const p0 = { x: srcCorners[0].x * srcW, y: srcCorners[0].y * srcH };
  const p1 = { x: srcCorners[1].x * srcW, y: srcCorners[1].y * srcH };
  const p2 = { x: srcCorners[2].x * srcW, y: srcCorners[2].y * srcH };
  const p3 = { x: srcCorners[3].x * srcW, y: srcCorners[3].y * srcH };
  const topW = Math.hypot(p1.x - p0.x, p1.y - p0.y);
  const botW = Math.hypot(p2.x - p3.x, p2.y - p3.y);
  const dstW = Math.round(Math.max(topW, botW));
  const leftH = Math.hypot(p3.x - p0.x, p3.y - p0.y);
  const rightH = Math.hypot(p2.x - p1.x, p2.y - p1.y);
  const dstH = Math.round(Math.max(leftH, rightH));
  dstCanvas.width = dstW;
  dstCanvas.height = dstH;
  const dstCtx = dstCanvas.getContext("2d", { willReadFrequently: true });
  if (!dstCtx) return;
  const srcImgData = srcCtx.getImageData(0, 0, srcW, srcH);
  const srcData = srcImgData.data;
  const dstImgData = dstCtx.createImageData(dstW, dstH);
  const dstData = dstImgData.data;
  const h = getInverseHomography(srcCorners, srcW, srcH, dstW, dstH);
  for (let y = 0; y < dstH; y++) {
    for (let x = 0; x < dstW; x++) {
      const w = h[6] * x + h[7] * y + 1;
      const u = (h[0] * x + h[1] * y + h[2]) / w;
      const v = (h[3] * x + h[4] * y + h[5]) / w;
      if (u >= 0 && u < srcW - 1 && v >= 0 && v < srcH - 1) {
        const u0 = Math.floor(u), u1 = u0 + 1, v0 = Math.floor(v), v1 = v0 + 1;
        const du = u - u0, dv = v - v0;
        const idx00 = (v0 * srcW + u0) * 4, idx10 = (v0 * srcW + u1) * 4;
        const idx01 = (v1 * srcW + u0) * 4, idx11 = (v1 * srcW + u1) * 4;
        const dstIdx = (y * dstW + x) * 4;
        for (let c = 0; c < 4; c++) {
          dstData[dstIdx + c] =
            srcData[idx00 + c] * (1 - du) * (1 - dv) +
            srcData[idx10 + c] * du * (1 - dv) +
            srcData[idx01 + c] * (1 - du) * dv +
            srcData[idx11 + c] * du * dv;
        }
      } else {
        const dstIdx = (y * dstW + x) * 4;
        dstData[dstIdx] = 255; dstData[dstIdx + 1] = 255; dstData[dstIdx + 2] = 255; dstData[dstIdx + 3] = 255;
      }
    }
  }
  dstCtx.putImageData(dstImgData, 0, 0);
}

export function applyImageFilters(srcCanvas, dstCanvas, config, rotation, flipH, flipV) {
  const srcCtx = srcCanvas.getContext("2d", { willReadFrequently: true });
  if (!srcCtx) return;
  const w = srcCanvas.width, h = srcCanvas.height;
  const angle = ((rotation % 360) + 360) % 360;
  const is90or270 = angle === 90 || angle === 270;
  dstCanvas.width = is90or270 ? h : w;
  dstCanvas.height = is90or270 ? w : h;
  const dstCtx = dstCanvas.getContext("2d", { willReadFrequently: true });
  if (!dstCtx) return;
  dstCtx.clearRect(0, 0, dstCanvas.width, dstCanvas.height);
  dstCtx.save();
  dstCtx.translate(dstCanvas.width / 2, dstCanvas.height / 2);
  dstCtx.rotate((angle * Math.PI) / 180);
  dstCtx.scale(flipH ? -1 : 1, flipV ? -1 : 1);
  dstCtx.drawImage(srcCanvas, -w / 2, -h / 2);
  dstCtx.restore();
  const imgData = dstCtx.getImageData(0, 0, dstCanvas.width, dstCanvas.height);
  const data = imgData.data;
  const len = data.length;
  const bFactor = config.brightness / 100;
  const cFactor = config.contrast > 0 ? (1 + config.contrast / 100) ** 2 : 1 + config.contrast / 100;
  const sFactor = config.saturation > 0 ? 1 + config.saturation / 50 : 1 + config.saturation / 100;
  for (let i = 0; i < len; i += 4) {
    let r = data[i], g = data[i + 1], b = data[i + 2], a = data[i + 3];
    if (a === 0) continue;
    switch (config.filterType) {
      case "grayscale": { const gray = 0.299 * r + 0.587 * g + 0.114 * b; r = g = b = gray; break; }
      case "bw": { const gray = 0.299 * r + 0.587 * g + 0.114 * b; const thresh = gray > 127 ? 255 : 0; r = g = b = thresh; break; }
      case "magic": {
        const maxVal = Math.max(r, g, b);
        if (maxVal > 150) { r = Math.min(255, r * 1.15); g = Math.min(255, g * 1.15); b = Math.min(255, b * 1.15); }
        else { r *= 0.9; g *= 0.9; b *= 0.9; }
        const l = 0.299 * r + 0.587 * g + 0.114 * b;
        r = l + (r - l) * 1.35; g = l + (g - l) * 1.35; b = l + (b - l) * 1.35;
        break;
      }
      case "high-contrast": { r = r > 128 ? Math.min(255, r * 1.2) : r * 0.8; g = g > 128 ? Math.min(255, g * 1.2) : g * 0.8; b = b > 128 ? Math.min(255, b * 1.2) : b * 0.8; break; }
      case "shadow-free": { const gray = 0.299 * r + 0.587 * g + 0.114 * b; if (gray < 200) { const boost = (200 - gray) * 0.35; r = Math.min(255, r + boost); g = Math.min(255, g + boost); b = Math.min(255, b + boost); } break; }
      case "scanner-auto": {
        const gray = 0.299 * r + 0.587 * g + 0.114 * b;
        const norm = Math.min(255, Math.max(0, ((gray - 50) / 170) * 255));
        if (norm > 160) { const w = Math.min(1, (norm - 160) / 95); r = Math.min(255, r + (255 - r) * w * 0.5); g = Math.min(255, g + (255 - g) * w * 0.5); b = Math.min(255, b + (255 - b) * w * 0.5); }
        const l2 = 0.299 * r + 0.587 * g + 0.114 * b;
        const ct = l2 > 140 ? 1.25 : 0.8; r = Math.min(255, Math.max(0, (r - 128) * ct + 128)); g = Math.min(255, Math.max(0, (g - 128) * ct + 128)); b = Math.min(255, Math.max(0, (b - 128) * ct + 128));
        break;
      }
      case "scanner-bw": {
        const gb = 0.299 * r + 0.587 * g + 0.114 * b;
        const norm = Math.min(255, Math.max(0, ((gb - 50) / 170) * 255));
        r = g = b = norm > 128 ? 255 : 0;
        break;
      }
      case "scanner-color": {
        const gray2 = 0.299 * r + 0.587 * g + 0.114 * b;
        if (gray2 > 130) { const w2 = Math.min(1, (gray2 - 130) / 125); r = Math.min(255, r + (255 - r) * w2 * 0.5); g = Math.min(255, g + (255 - g) * w2 * 0.5); b = Math.min(255, b + (255 - b) * w2 * 0.5); }
        const l3 = 0.299 * r + 0.587 * g + 0.114 * b;
        r = l3 + (r - l3) * 1.3; g = l3 + (g - l3) * 1.3; b = l3 + (b - l3) * 1.3;
        const ct2 = l3 > 140 ? 1.15 : 0.88; r = Math.min(255, Math.max(0, (r - 128) * ct2 + 128)); g = Math.min(255, Math.max(0, (g - 128) * ct2 + 128)); b = Math.min(255, Math.max(0, (b - 128) * ct2 + 128));
        break;
      }
    }
    if (bFactor !== 0) { r += bFactor * 255; g += bFactor * 255; b += bFactor * 255; }
    if (config.contrast !== 0) { r = (r - 128) * cFactor + 128; g = (g - 128) * cFactor + 128; b = (b - 128) * cFactor + 128; }
    if (config.saturation !== 0 && config.filterType !== "grayscale" && config.filterType !== "bw") { const l = 0.299 * r + 0.587 * g + 0.114 * b; r = l + (r - l) * sFactor; g = l + (g - l) * sFactor; b = l + (b - l) * sFactor; }
    data[i] = Math.max(0, Math.min(255, r));
    data[i + 1] = Math.max(0, Math.min(255, g));
    data[i + 2] = Math.max(0, Math.min(255, b));
  }
  dstCtx.putImageData(imgData, 0, 0);
  if (config.sharpness > 0) {
    const finalImgData = dstCtx.getImageData(0, 0, dstCanvas.width, dstCanvas.height);
    const rawData = new Uint8ClampedArray(finalImgData.data);
    const finalData = finalImgData.data;
    const w_f = dstCanvas.width, h_f = dstCanvas.height, sharpAmt = config.sharpness / 100;
    for (let y = 0; y < h_f; y++) {
      for (let x = 0; x < w_f; x++) {
        const idx = (y * w_f + x) * 4;
        for (let c = 0; c < 3; c++) {
          const center = rawData[idx + c];
          const top = rawData[(Math.max(0, y - 1) * w_f + x) * 4 + c];
          const left = rawData[(y * w_f + Math.max(0, x - 1)) * 4 + c];
          const right = rawData[(y * w_f + Math.min(w_f - 1, x + 1)) * 4 + c];
          const bottom = rawData[(Math.min(h_f - 1, y + 1) * w_f + x) * 4 + c];
          const laplacian = center * 5 - (top + left + right + bottom);
          finalData[idx + c] = Math.max(0, Math.min(255, center + sharpAmt * (laplacian - center)));
        }
      }
    }
    dstCtx.putImageData(finalImgData, 0, 0);
  }
}

export function validateQuality(canvas) {
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  if (!ctx) return { isBlurry: false, blurScore: 80, isTooDark: false, isTooBright: false, brightnessScore: 128, overallScore: 80 };
  const w = canvas.width, h = canvas.height;
  const imgData = ctx.getImageData(0, 0, w, h);
  const data = imgData.data;
  const step = Math.max(1, Math.floor((w * h) / 10000));
  let totalLuminance = 0, sampleCount = 0;
  for (let i = 0; i < data.length; i += step * 4) {
    const r = data[i], g = data[i + 1], b = data[i + 2], a = data[i + 3];
    if (a === 0) continue;
    totalLuminance += 0.299 * r + 0.587 * g + 0.114 * b;
    sampleCount++;
  }
  const avgBrightness = sampleCount > 0 ? totalLuminance / sampleCount : 128;
  const isTooDark = avgBrightness < 50;
  const isTooBright = avgBrightness > 215;
  let totalDiff = 0, gradientSamples = 0;
  const sampleRows = Math.min(h - 2, 100);
  const rowStep = Math.max(1, Math.floor(h / sampleRows));
  const colStep = Math.max(1, Math.floor(w / 100));
  for (let y = 1; y < h - 1; y += rowStep) {
    for (let x = 1; x < w - 1; x += colStep) {
      const idx = (y * w + x) * 4;
      const val = 0.299 * data[idx] + 0.587 * data[idx + 1] + 0.114 * data[idx + 2];
      const rightIdx = (y * w + x + 1) * 4;
      const rightVal = 0.299 * data[rightIdx] + 0.587 * data[rightIdx + 1] + 0.114 * data[rightIdx + 2];
      const downIdx = ((y + 1) * w + x) * 4;
      const downVal = 0.299 * data[downIdx] + 0.587 * data[downIdx + 1] + 0.114 * data[downIdx + 2];
      totalDiff += Math.abs(val - rightVal) + Math.abs(val - downVal);
      gradientSamples += 2;
    }
  }
  const avgGradient = gradientSamples > 0 ? totalDiff / gradientSamples : 5;
  const blurScore = Math.min(100, Math.round(avgGradient * 5));
  const isBlurry = blurScore < 25;
  let overallScore = 100;
  if (isBlurry) overallScore -= 40;
  if (isTooDark) overallScore -= 30;
  if (isTooBright) overallScore -= 20;
  if (w < 800 || h < 800) overallScore -= 20;
  overallScore = Math.max(10, overallScore);
  return { isBlurry, blurScore, isTooDark, isTooBright, brightnessScore: Math.round(avgBrightness), overallScore };
}

function smoothArray(arr, len, radius) {
  const out = new Float32Array(len);
  for (let i = 0; i < len; i++) {
    let sum = 0, count = 0;
    for (let j = Math.max(0, i - radius); j <= Math.min(len - 1, i + radius); j++) { sum += arr[j]; count++; }
    out[i] = sum / count;
  }
  return out;
}

function morphClose(binary, w, h, passes) {
  let data = new Uint8Array(binary);
  for (let p = 0; p < passes; p++) {
    const next = new Uint8Array(data);
    for (let y = 2; y < h - 2; y++) {
      for (let x = 2; x < w - 2; x++) {
        let any = 0;
        for (let dy = -2; dy <= 2; dy++) for (let dx = -2; dx <= 2; dx++) if (data[(y+dy)*w+(x+dx)]) { any = 1; break; }
        if (any) next[y*w+x] = 1;
      }
    }
    for (let y = 2; y < h - 2; y++) {
      for (let x = 2; x < w - 2; x++) {
        if (!next[y*w+x]) continue;
        let all = 1;
        for (let dy = -2; dy <= 2; dy++) for (let dx = -2; dx <= 2; dx++) if (!next[(y+dy)*w+(x+dx)]) { all = 0; break; }
        if (!all) next[y*w+x] = 0;
      }
    }
    data = next;
  }
  return data;
}

function convexHull(points) {
  const sorted = [...points].sort((a, b) => a.x - b.x || a.y - b.y);
  const cross = (O, A, B) => (A.x - O.x) * (B.y - O.y) - (A.y - O.y) * (B.x - O.x);
  const lower = [];
  for (const p of sorted) { while (lower.length >= 2 && cross(lower[lower.length-2], lower[lower.length-1], p) <= 0) lower.pop(); lower.push(p); }
  const upper = [];
  for (let i = sorted.length - 1; i >= 0; i--) { const p = sorted[i]; while (upper.length >= 2 && cross(upper[upper.length-2], upper[upper.length-1], p) <= 0) upper.pop(); upper.push(p); }
  lower.pop(); upper.pop();
  return lower.concat(upper);
}

function hullCorners(hull) {
  if (hull.length <= 4) return hull;
  const cx = hull.reduce((s, p) => s + p.x, 0) / hull.length;
  const cy = hull.reduce((s, p) => s + p.y, 0) / hull.length;

  const f1 = hull.reduce((b, p) => { const d = (p.x-cx)**2+(p.y-cy)**2; return d > b.d ? { p, d } : b; }, { p: hull[0], d: 0 }).p;
  const f2 = hull.reduce((b, p) => { const d = (p.x-f1.x)**2+(p.y-f1.y)**2; return d > b.d ? { p, d } : b; }, { p: hull[0], d: 0 }).p;

  const dx = f2.x - f1.x, dy = f2.y - f1.y, lenSq = dx*dx + dy*dy;
  let best1 = f1, best2 = f2, max1 = 0, max2 = 0;
  const crossVal = (p) => (p.x-f1.x)*dy - (p.y-f1.y)*dx;
  for (const p of hull) {
    if (p === f1 || p === f2) continue;
    const t = Math.max(0, Math.min(1, ((p.x-f1.x)*dx + (p.y-f1.y)*dy) / lenSq));
    const px = f1.x + t*dx, py = f1.y + t*dy;
    const d = (p.x-px)**2 + (p.y-py)**2;
    if (crossVal(p) > 0) { if (d > max1) { max1 = d; best1 = p; } }
    else { if (d > max2) { max2 = d; best2 = p; } }
  }

  const result = [f1, best1, f2, best2];
  const acx = result.reduce((s, p) => s + p.x, 0) / 4;
  const acy = result.reduce((s, p) => s + p.y, 0) / 4;
  result.sort((a, b) => Math.atan2(a.y-acy, a.x-acx) - Math.atan2(b.y-acy, b.x-acx));
  const tl = result.reduce((b, p) => (p.x + p.y) < (b.x + b.y) ? p : b);
  const ti = result.indexOf(tl);
  return [result[ti], result[(ti+1)%4], result[(ti+2)%4], result[(ti+3)%4]];
}

export function autoDetectDocumentCorners(w, h, imageSource) {
  const defaultPoints = [{ x: 0.05, y: 0.05 }, { x: 0.95, y: 0.05 }, { x: 0.95, y: 0.95 }, { x: 0.05, y: 0.95 }];
  if (!imageSource) return defaultPoints;
  try {
    const dw = 400, dh = 400;
    const canvas = document.createElement("canvas");
    canvas.width = dw; canvas.height = dh;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    if (!ctx) return defaultPoints;
    ctx.drawImage(imageSource, 0, 0, dw, dh);
    const imgData = ctx.getImageData(0, 0, dw, dh);
    const data = imgData.data;

    const gray = new Float32Array(dw * dh);
    for (let i = 0; i < dw * dh; i++) {
      const idx = i * 4;
      gray[i] = 0.299 * data[idx] + 0.587 * data[idx + 1] + 0.114 * data[idx + 2];
    }

    const rowBright = new Float32Array(dh);
    for (let y = 0; y < dh; y++) { let s = 0; for (let x = 0; x < dw; x++) s += gray[y*dw+x]; rowBright[y] = s / dw; }
    const sRow = smoothArray(rowBright, dh, 5);
    const docCenter = sRow[Math.round(dh/2)];
    const bgEdge = (sRow[0] + sRow[dh-1]) / 2;
    const rawThresh = (docCenter + bgEdge) / 2;
    const thresh = Math.max(80, Math.min(rawThresh, 220));

    const binary = new Uint8Array(dw * dh);
    for (let i = 0; i < dw * dh; i++) binary[i] = gray[i] > thresh ? 1 : 0;

    const closed = morphClose(binary, dw, dh, 1);

    const points = [];
    for (let y = 0; y < dh; y++) for (let x = 0; x < dw; x++) if (closed[y*dw+x]) points.push({ x, y });

    if (points.length < dw * dh * 0.03) return defaultPoints;

    const hull = convexHull(points);
    if (hull.length < 4) return defaultPoints;

    const corners = hullCorners(hull);
    const docW = Math.max(...corners.map(c => c.x)) - Math.min(...corners.map(c => c.x));
    const docH = Math.max(...corners.map(c => c.y)) - Math.min(...corners.map(c => c.y));
    if (docW < dw * 0.15 || docH < dh * 0.15) return defaultPoints;

    return corners.map(c => ({
      x: Math.max(0, Math.min(1, c.x / dw)),
      y: Math.max(0, Math.min(1, c.y / dh)),
    }));
  } catch (e) { console.error("Auto corner detection failed:", e); }
  return defaultPoints;
}

export function removeBackgroundEdges(canvas) {
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  if (!ctx) return;
  const w = canvas.width, h = canvas.height;
  if (w < 10 || h < 10) return;
  const imgData = ctx.getImageData(0, 0, w, h);
  const data = imgData.data;

  const band = Math.min(30, Math.max(2, Math.round(Math.min(w, h) * 0.015)));

  const corners = [
    { x: 0, y: 0 }, { x: w - 1, y: 0 }, { x: w - 1, y: h - 1 }, { x: 0, y: h - 1 },
  ];
  const samples = [];
  corners.forEach(({ x, y }) => {
    for (let dy = 0; dy < band; dy++) {
      for (let dx = 0; dx < band; dx++) {
        const px = Math.min(w - 1, x + dx), py = Math.min(h - 1, y + dy);
        const idx = (py * w + px) * 4;
        samples.push({ r: data[idx], g: data[idx + 1], b: data[idx + 2] });
      }
    }
  });

  if (samples.length === 0) return;

  samples.sort((a, b) => (a.r + a.g + a.b) - (b.r + b.g + b.b));
  const median = samples[Math.floor(samples.length / 2)];
  const bgR = median.r, bgG = median.g, bgB = median.b;

  const centerIdx = (Math.floor(h / 2) * w + Math.floor(w / 2)) * 4;
  const docR = data[centerIdx], docG = data[centerIdx + 1], docB = data[centerIdx + 2];
  const docBrightness = 0.299 * docR + 0.587 * docG + 0.114 * docB;
  const bgBrightness = 0.299 * bgR + 0.587 * bgG + 0.114 * bgB;

  if (Math.abs(docBrightness - bgBrightness) < 30) return;

  const isBgPixel = (r, g, b) => {
    const dr = r - bgR, dg = g - bgG, db = b - bgB;
    const dist = Math.sqrt(dr * dr + dg * dg + db * db);
    const bright = 0.299 * r + 0.587 * g + 0.114 * b;
    return dist < 80 && Math.abs(bright - bgBrightness) < 50;
  };

  for (let y = 0; y < band; y++) {
    const fade = 1 - (y / band);
    for (let x = 0; x < w; x++) {
      const idx = (y * w + x) * 4;
      if (isBgPixel(data[idx], data[idx + 1], data[idx + 2])) {
        data[idx] = Math.round(data[idx] + (255 - data[idx]) * fade);
        data[idx + 1] = Math.round(data[idx + 1] + (255 - data[idx + 1]) * fade);
        data[idx + 2] = Math.round(data[idx + 2] + (255 - data[idx + 2]) * fade);
      }
    }
  }
  for (let y = h - band; y < h; y++) {
    const fade = 1 - ((h - 1 - y) / band);
    for (let x = 0; x < w; x++) {
      const idx = (y * w + x) * 4;
      if (isBgPixel(data[idx], data[idx + 1], data[idx + 2])) {
        data[idx] = Math.round(data[idx] + (255 - data[idx]) * fade);
        data[idx + 1] = Math.round(data[idx + 1] + (255 - data[idx + 1]) * fade);
        data[idx + 2] = Math.round(data[idx + 2] + (255 - data[idx + 2]) * fade);
      }
    }
  }
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < band; x++) {
      const idx = (y * w + x) * 4;
      if (isBgPixel(data[idx], data[idx + 1], data[idx + 2])) {
        const fade = 1 - (x / band);
        data[idx] = Math.round(data[idx] + (255 - data[idx]) * fade);
        data[idx + 1] = Math.round(data[idx + 1] + (255 - data[idx + 1]) * fade);
        data[idx + 2] = Math.round(data[idx + 2] + (255 - data[idx + 2]) * fade);
      }
    }
  }
  for (let y = 0; y < h; y++) {
    for (let x = w - band; x < w; x++) {
      const idx = (y * w + x) * 4;
      if (isBgPixel(data[idx], data[idx + 1], data[idx + 2])) {
        const fade = 1 - ((w - 1 - x) / band);
        data[idx] = Math.round(data[idx] + (255 - data[idx]) * fade);
        data[idx + 1] = Math.round(data[idx + 1] + (255 - data[idx + 1]) * fade);
        data[idx + 2] = Math.round(data[idx + 2] + (255 - data[idx + 2]) * fade);
      }
    }
  }

  ctx.putImageData(imgData, 0, 0);
}
